import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Ad } from '@/__generated_graphql__/types';
import {
  ActivePaidService,
  CARD_IDS
} from '@/components/AdvertisePayments/components/ExtraContent/ExtraContent.types';
import { AD_TYPE, PaymentTypeEnum, ROUTES } from '@/constants/common';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';

import { useModal } from './useModal';
import { usePaymentModalsManager } from './usePaymentModalsManager';

export type UseModalControllerProps = {
  mainModal: ReturnType<typeof useModal>;
  uiModals: ReturnType<typeof usePaymentModalsManager>;
  type?: PaymentTypeEnum;
};

export const useModalController = ({
  mainModal,
  uiModals,
  type = PaymentTypeEnum.ADVERTISE
}: UseModalControllerProps) => {
  const [isAddBalance, setIsAddBalance] = useState<boolean>(false);

  const [activeCard, setActiveCard] = useState<string>(CARD_IDS.TOP);
  const [activePeriodId, setActivePeriodId] = useState<number | null>(null);
  const [shouldRender, setShouldRender] = useState<boolean>(mainModal.isOpen);
  const [isVisible, setIsVisible] = useState<boolean>(mainModal.isOpen);
  const modalOverlayRef = useRef<HTMLDivElement | null>(null);
  const [activePaidServiceId, setActivePaidServiceId] =
    useState<ActivePaidService | null>(null);

  const [isExtraContentVisible, setExtraContentVisible] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const router = useRouter();

  useBodyScrollLock(mainModal.isOpen || uiModals.isAnotherModal);

  const onAddBalance = useCallback(() => {
    mainModal.open();
    setExtraContentVisible(true);
    setIsAddBalance(true);
    uiModals.closeAll();
  }, [mainModal.isOpen, setIsAddBalance]);

  useEffect(() => {
    if (mainModal.isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      setShouldRender(false);
      setIsAddBalance(false);
    }
  }, [mainModal.isOpen]);

  useEffect(() => {
    setActivePeriodId(null);
  }, [activeCard]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        mainModal.close();
      }
    };

    if (mainModal.isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mainModal]);

  useEffect(() => {
    if (type === PaymentTypeEnum.TOP_UP_BALANCE && mainModal.isOpen) {
      onAddBalance();
    }
  }, [type, mainModal.isOpen]);

  const onContinue = useCallback(() => {
    setExtraContentVisible(true);
  }, []);

  const onPay = useCallback(
    (adId: string, adType: Ad['__typename'], paymentType: string) => {
      if (type === PaymentTypeEnum.ADVERTISE) {
        onContinue();
        return;
      }

      const computedType =
        adType === AD_TYPE.RESUME ? ROUTES.RESUME_PAY : ROUTES.VACANCY_PAY;

      router.push(computedType(adId, paymentType));
    },
    []
  );

  const closeExtraContent = useCallback(() => {
    setExtraContentVisible(false);
  }, []);

  const reset = useCallback(() => {
    mainModal.close();
    Promise.resolve().then(() => {
      setExtraContentVisible(false);
    });
  }, [mainModal]);

  const handleClickOutside = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        reset();
      }
    },
    [reset]
  );

  return {
    activeCard,
    activePaidServiceId,
    activePeriodId,
    isExtraContentVisible,
    shouldRender,
    isVisible,
    modalOverlayRef,
    uiModals,
    isLoading,
    isAddBalance,
    setIsLoading,
    onContinue,
    onAddBalance,
    onPay,
    setActiveCard,
    setActivePeriodId,
    setActivePaidServiceId,
    setExtraContentVisible,
    setIsAddBalance,
    closeExtraContent,
    handleClickOutside,
    reset
  };
};
